import gql from 'graphql-tag';
import promotionFragment from './store/fragments/promotion';
import entityFragment from './store/fragments/entity-interface';
import claimedPromotionFragment from './store/fragments/claimed-promotion.js';
import { getIds } from './utils';
import { parsePromotions } from './routes/utils';
import { isIE11, isEdge15 } from '../../../app/analytics';
import { VIEW } from '@updater-core/lib/tracker';
import '@updater-components/tooltip';
import '../../../tiled-header';

const redirectToLandingPageIfComingFromUnlocked = (router) => {
  return router.lastPathGoneTo && router.lastPathGoneTo.includes('unlocked')
    ? '/'
    : '';
};

const redirectToLandingPageIfNotEligible = (router, { store }) => {
  const [promotionId, entityId] = getIds(
    router.desiredPath || router.currentPath
  );
  const {
    eligiblePromotions: { eligiblePromotions },
  } = store.readQuery({
    query: gql`
      query getPromotion {
        eligiblePromotions {
          eligiblePromotions {
            promotion {
              ...${promotionFragment.name}
            }
            validFor {
              ...${entityFragment.name}
            }
          }
        }
      }
      ${promotionFragment.fragment}
      ${entityFragment.fragment}
  `,
  });
  const { exclusive: exclusivePromotions } = parsePromotions(
    eligiblePromotions
  );
  const isEligibleForPromotion = exclusivePromotions.some(
    (exclusivePromotion) => {
      return (
        exclusivePromotion.promotion.id === promotionId &&
        exclusivePromotion.validFor.filter((entity) => entity.id === entityId)
          .length
      );
    }
  );
  return isEligibleForPromotion ? '' : '/';
};

const redirectToDealsPageIfAnyPromotionsHaveAlreadyBeenClaimed = (
  router,
  { store }
) => {
  return store
    .query({
      query: gql`
      query checkIfUserHasClaimedPromos($moveId: ID) {
        claimedPromotions(moveId: $moveId) {
          claimedPromotions {
            ...${claimedPromotionFragment.name}
          }
        }
      }
      ${claimedPromotionFragment.fragment}
    `,
      variables: {
        moveId: store.moveId(),
      },
    })
    .then(({ data: { claimedPromotions = {} } }) => {
      if (
        claimedPromotions.claimedPromotions &&
        claimedPromotions.claimedPromotions.length
      ) {
        return '/deals?wallet';
      }

      return false;
    })
    .catch((e) => {
      // we want to fall back to just routing to the main page because that's most
      // important.
      console.log(e);
      return false;
    });
};

const header = document.createElement('upd-tiled-header');

const tooltipText = `Simply claim your favorite offers and let the savings begin. To ensure the best service, brands may contact you directly for redemption, setup, service appointments, and more. Please read each offer's terms carefully.`;

header.innerHTML = `
  <style>
    upd-tiled-header {
      z-index: 5;
      color: #2b7499;
      padding: 16px;
      line-height: 1.5;
      border-top: solid 1px #80acc2;
      border-bottom: solid 1px #80acc2;
    }
    .header,
    .description,
    .tooltip-description {
        width: calc(100% - 40px);
        margin: 0 auto;
        letter-spacing: normal;
        max-width: 480px;
      }
    .header {
      font-size: 32px;
      font-weight: 900;
    }
    .tooltip-description {
      display: flex;
      align-items: center;
    }
    .tooltip-description:not(svg) {
      text-decoration: underline;
    }
    svg {
      padding-right: 5px;
    }
    upd-tooltip {
      width: 280px;
    }
    @media(max-width: 480px) {
      .header {
        font-size: 24px;
      }
      .header,
      .description,
      .tooltip-description {
          width: auto;
        }
      .description {
        display: inline
      }
      .tooltip-description {
        display: inline-flex;
      }
    }
  </style>
  <div class="header">Browse Discounts</div>
  <div class="description">Get instant savings for all the things you need.</div>
  <div class=
  tooltip-description>
    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="16" height="16">
    <defs>
      <path id="a" d="M0 0h15.925v15.925H0z"/>
    </defs>
    <g fill="none" fill-rule="evenodd">
      <mask id="b" fill="#fff">
        <use xlink:href="#a"/>
      </mask>
      <path fill="#80ACC2" d="M8.99 9.156v.734H6.733V8.822c0-1.228 1.93-1.921 1.93-2.655 0-.508-.473-.83-.898-.83-.522 0-.95.454-1.363.988L4.984 5.01c.85-1.08 1.833-1.754 3.047-1.754 1.615 0 2.89 1.137 2.89 2.938 0 1.735-1.93 2.162-1.93 2.963m-1.16 3.883a1.316 1.316 0 1 1 0-2.632 1.316 1.316 0 0 1 0 2.632m.132 2.886a7.924 7.924 0 0 0 4.514-1.404l2.958.792-.804-3A7.962 7.962 0 0 0 7.962 0a7.962 7.962 0 1 0 0 15.925" mask="url(#b)"/>
    </g> 
  </svg>
  <upd-tooltip
    enabled
    position="bottom"
    label="${tooltipText}"
  >
    How does this work?
  </upd-tooltip>
</div>
`;

function defaultHeader() {
  return header;
}

export const CONTENT_SLOT = 'OFFERS-CONTENT-SLOT';
export const HEADER_SLOT = 'OFFERS-HEADER-SLOT';
const CONTENT = {
  OFFERS_PAGE: 'offers-page',
  NO_OFFERS_PAGE: 'no-offers-page',
  NEW_OFFERS_PAGE: 'new-offers-page',
  SPLASH_PAGE: 'splash',
  CONFIRM_PAGE: 'confirm-offer-page',
  UNLOCKED_PAGE: 'unlocked-offer-page',
};

export const isTerminalContent = (content) => content !== CONTENT.CONFIRM_PAGE;

const offersPage = {
  components: {
    [`#${CONTENT_SLOT}`]: CONTENT.OFFERS_PAGE,
    [`#${HEADER_SLOT}`]: '',
  },
  data: {
    analytics: {
      event: 'Move-In Offers',
    },
  },
};

const noOffersPage = {
  components: {
    [`#${CONTENT_SLOT}`]: CONTENT.NO_OFFERS_PAGE,
    [`#${HEADER_SLOT}`]: '',
  },
  data: {
    analytics: {
      event: 'No Offers',
    },
  },
};

const newLayout = {
  components: {
    [`#${CONTENT_SLOT}`]: CONTENT.NEW_OFFERS_PAGE,
    [`#${HEADER_SLOT}`]: defaultHeader,
  },
  data: {
    analytics: {
      event: 'offers_page',
    },
  },
};

const splash = {
  components: {
    [`#${CONTENT_SLOT}`]: CONTENT.SPLASH_PAGE,
    [`#${HEADER_SLOT}`]: '',
  },
  data: {
    analytics: {
      event: 'Splash Page',
    },
  },
};

const defaultRoutes = {
  '/.*/confirm': {
    components: {
      [`#${CONTENT_SLOT}`]: CONTENT.CONFIRM_PAGE,
      [`#${HEADER_SLOT}`]: '',
    },
    redirects: [
      redirectToLandingPageIfComingFromUnlocked,
      redirectToLandingPageIfNotEligible,
    ],
    data: {
      analytics: {
        event: 'Offer Confirm',
      },
    },
  },
  '/unlocked/.*': {
    components: {
      [`#${CONTENT_SLOT}`]: CONTENT.UNLOCKED_PAGE,
      [`#${HEADER_SLOT}`]: '',
    },
    data: {
      analytics: {
        event: 'Offer Claimed',
      },
    },
  },
  '/no-offers': noOffersPage,
  '/splash(?:\\?ids=(.*))?': splash,
};

const landingPageRedirects = [
  (_, { showSplashPage }) => (showSplashPage ? '/splash' : false),
];
export function getRoutes({ tracker }) {
  const showOldLayout =
    isIE11 || isEdge15 || localStorage.getItem('cypress_test_old_layout');
  if (showOldLayout) {
    // The router needs to be setup for the tracker to track correctly so deffer
    // this
    setTimeout(() =>
      tracker.track({
        verb: VIEW,
        object: 'legacy_offers_page',
        details: {
          isIE11,
          isEdge15,
          showOldLayout,
        },
      })
    );
  }

  return showOldLayout
    ? Object.assign(defaultRoutes, {
        '/': {
          ...offersPage,
          redirects: landingPageRedirects,
        },
      })
    : Object.assign(defaultRoutes, {
        '/': {
          ...newLayout,
          redirects: landingPageRedirects,
        },
      });
}
